<template>
	<BaseLoading v-if="isLoading" is-full-page />
	<div v-else>
		<div class="page-header">
			<CRow class="align-items-center">
				<CCol md="8">
					<h2 class="typo-h4 d-flex align-items-center mb-0">
						<span>
							{{ info.nameEn || info.name }}
						</span>
						<CBadge
							:color="periodStatus.class"
							class="badge-status"
						>
							{{ periodStatus.title }}
						</CBadge>
					</h2>
				</CCol>
				<CCol md="4" class="text-right">
					<button
						class="btn btn-primary"
						@click="onClickViewWebsite"
					>
						<FontAwesomeIcon :icon="['fas', 'check-circle']" class="mr-2" />
						View website
					</button>
				</CCol>
			</CRow>
			<CRow class="summary">
				<CCol md="12" lg="8">
					<p class="typo-body-2 m-0 p-0">
						Last updated by: <span class="color-hypertext">{{ updatedBy }}</span><span class="date color-black-45">{{ info.updatedAt }}</span>
					</p>
					<p class="typo-body-2 m-0">
						Created by: <span class="color-hypertext">{{ createdBy }}</span><span class="date color-black-45">{{ info.createdAt }}</span>
					</p>
				</CCol>
			</CRow>
		</div>
		<div class="tabs-container-large mt-4 mb-5 pb-5">
			<div v-if="steps" class="tabs-wrapper">
				<CTabs
					:active-tab="activeTab"
					@update:activeTab="handleUpdateTab"
				>
					<CTab
						v-for="(step, key) in steps"
						:key="key"
						:title="`Step ${key}: ${step.title}`"
						:disabled="step.disabled"
					>
						<div :class="{ 'main-wrapper': key === '1', 'c-main': key === '2' }">
							<div :class="{ 'container-fluid': key === '2' }">
								<component
									:is="step.component"
									:default-data="step.formData"
									:is-edit-mode="true"
									@update:is-valid="handleValidChange(key, $event)"
								/>
							</div>
						</div>
					</CTab>
				</CTabs>
			</div>
			<div class="navigation-panel">
				<div class="main-wrapper">
					<CRow>
						<CCol
							lg="12"
							class="d-flex justify-content-end align-items-center"
						>
							<a
								href="#"
								class="btn-remove mr-auto"
								@click.prevent="handleRemove"
							>
								Remove Static Landing Page
							</a>
							<span class="mr-4">Step {{ currentStep }}/{{ totalStep }}</span>
							<CButton
								color="tertiary"
								class="transparent mr-3"
								@click.prevent="$router.push({ name: 'StaticLandingPage'})"
							>
								Cancel
							</CButton>
							<CButton
								type="button"
								class="transparent"
								color="primary"
								:disabled="!isEnableNextStep || isSubmitting"
								@click="handleClickNextStep"
							>
								{{ confirmText }}
							</CButton>
						</CCol>
					</CRow>
				</div>
			</div>
		</div>
		<BaseModalConfirmDelete
			ref="modal-confirm-delete"
			:handle-remove="deleteStaticLandingPage.bind(null, $route.params.id)"
			title="Remove this Static Landing Page?"
			description="Are you sure you want to delete this Static Landing Page?"
			@onSuccess="$router.push({ name: 'StaticLandingPage' })"
		/>
	</div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import StaticLandingPageStepGeneralInfo from '@/components/StaticLandingPageStepGeneralInfo.vue';
import StaticLandingPageStepWidgetContent from '@/components/StaticLandingPageStepWidgetContent.vue';
import BaseModalConfirmDelete from '@/components/BaseModalConfirmDelete.vue';
import {
	transformedStaticLandingPageDataToAPI,
	transformedStaticLandingPageToDataMultipleStep,
	transformStaticLandingPage,
} from '../assets/js/transform/staticLandingPage';
import { STATIC_LANDING_PAGE_STATUSES } from '../enums/staticLandingPage';
import { DEFAULT_SYSTEM_ADMIN_NAME } from '../enums/general';
import { datetimeFormat } from '../assets/js/helpers';

export default {
	name: 'StaticLandingPageEdit',

	components: {
		BaseModalConfirmDelete,
	},

	data() {
		return {
			currentStep: 1,
			totalStep: 0,
			steps: null,
			isSubmitting: false,
			isLoading: true,
		};
	},

	computed: {
		...mapState('staticLandingPage', {
			edit: 'edit',
		}),

		activeTab() {
			return this.currentStep - 1;
		},

		info() {
			const transformed = transformStaticLandingPage(this.edit.data);
			return {
				...transformed,
				createdAt: this.edit.data?.createdAt ? datetimeFormat(this.edit.data.createdAt, 'DD MMM YYYY, HH:mm') : '-',
				updatedAt: this.edit.data?.updatedAt ? datetimeFormat(this.edit.data.updatedAt, 'DD MMM YYYY, HH:mm') : '-',
			};
		},

		updatedBy() {
			if (this.edit && this.edit.data && this.edit.data.updatedBy) {
				const { profile: { first_name: firstName, last_name: lastName } } = this.edit.data.updated_by;

				return [firstName, lastName].filter(Boolean).join(' ') || this.edit.data.updated_by.username;
			}

			return DEFAULT_SYSTEM_ADMIN_NAME;
		},

		createdBy() {
			if (this.edit && this.edit.data && this.edit.data.created_by) {
				const { profile: { first_name: firstName, last_name: lastName } } = this.edit.data.created_by;

				return [firstName, lastName].filter(Boolean).join(' ') || this.edit.data.created_by.username;
			}

			return DEFAULT_SYSTEM_ADMIN_NAME;
		},

		periodStatus() {
			return STATIC_LANDING_PAGE_STATUSES[this.info.isActive ? 1 : 0] || {};
		},

		feUrl() {
			return `${process.env.VUE_APP_ECOM_ENDPOINT}/th/pages/${this.info.slug || ''}`;
		},

		isEnableNextStep() {
			if (!this.steps) return false;

			const nextStep = this.currentStep + 1;
			if (this.currentStep === this.totalStep) {
				return this.steps[this.currentStep].valid;
			}

			return !this.steps[nextStep].disabled && this.steps[this.currentStep].valid;
		},

		confirmText() {
			if (this.isSubmitting) {
				return 'Updating...';
			}

			return this.currentStep === this.totalStep ? 'Update' : 'Next';
		},
	},

	async created() {
		const { id } = this.$route.params;
		await this.fetchData(id);
		this.initialStep();
		this.extractDataToStep();
	},

	methods: {
		...mapActions({
			getStaticLandingPage: 'staticLandingPage/getStaticLandingPage',
			updateStaticLandingPage: 'staticLandingPage/updateStaticLandingPage',
			deleteStaticLandingPage: 'staticLandingPage/deleteStaticLandingPage',
			showToast: 'toast/showToast',
		}),

		async fetchData(id) {
			this.isLoading = true;
			try {
				await this.getStaticLandingPage(id);
			} catch (error) {
				this.showToast({
					title: 'Error',
					message: 'Failed to fetch static landing page information.',
					variant: 'danger',
				});
				this.$router.push({ name: 'StaticLandingPage' });
			} finally {
				this.isLoading = false;
			}
		},
		handleRemove() {
			this.$refs['modal-confirm-delete'].open();
		},

		initialStep() {
			this.steps = {
				1: {
					title: 'General Info',
					valid: true,
					disabled: false,
					component: StaticLandingPageStepGeneralInfo,
					formData: null,
				},
				2: {
					title: 'Widget Content',
					valid: true,
					disabled: false,
					component: StaticLandingPageStepWidgetContent,
					formData: null,
				},
			};

			this.totalStep = Object.keys(this.steps).length;
		},

		extractDataToStep() {
			if (!this.edit.data) return;

			const landingPageData = transformedStaticLandingPageToDataMultipleStep(this.edit.data);
			if (landingPageData) {
				this.steps[1].formData = landingPageData.steps[1].formData;
				// Step 2 formData is null as widgets are fetched by the component
			}
		},

		async handleClickNextStep() {
			if (this.currentStep === this.totalStep) {
				await this.submitForm();
				return;
			}

			if (this.isEnableNextStep) {
				this.currentStep += 1;
			}
		},

		handleUpdateTab(tabIndex) {
			this.currentStep = tabIndex + 1;
		},

		handleValidChange(key, value) {
			if (!key) return;

			const { valid, data: formData } = value;

			this.$set(this.steps, key, { ...this.steps[key], valid, formData });

			const nextIndex = Number(key) + 1;
			if (nextIndex <= this.totalStep) {
				this.$set(this.steps, (nextIndex), { ...this.steps[nextIndex], disabled: !valid });
			}
		},

		async submitForm() {
			this.isSubmitting = true;
			const { id } = this.$route.params;
			const postData = transformedStaticLandingPageDataToAPI(this.steps);

			try {
				await this.updateStaticLandingPage({ id, data: postData });

				this.showToast({
					header: 'Success',
					content: 'Static landing page updated successfully.',
					type: 'success',
				});

				await this.fetchData(id);
				this.extractDataToStep();
			} catch (error) {
				const { data } = error.response || {};
				this.showToast({
					header: 'Error',
					content: data?.message || 'Something went wrong. Please try again later.',
					type: 'danger',
				});
			} finally {
				this.isSubmitting = false;
			}
		},

		onClickViewWebsite() {
			window.open(this.feUrl, '_blank');
		},
	},
};
</script>

<style lang="scss" scoped>
	.summary {
		.date {
			&::before {
				content: "|";
				display: inline-block;
				margin-left: rem(5);
				margin-right: rem(5);
			}
		}
	}

	.badge-status {
		width: auto;
		padding: rem(7) rem(8);
		margin-left: rem(16);
		font-size: rem(14);
	}

	.tabs-container-large {
		.tabs-wrapper {
			::v-deep & > div > div:first-child {
				margin-top: 0;
				padding-top: rem(32);
			}
		}
	}

	.navigation-panel {
		position: fixed;
		left: rem(56);
		right: 0;
		bottom: 0;
		padding: rem(12) 0;
		box-shadow: 0 1px 2px 0 $color-gray-07, 0 4px 16px 0 $color-gray-15;
		background-color: $color-white;
		z-index: 9;

		@media (max-width: 991.98px) {
			left: 0;
		}

		::v-deep button {
			min-width: rem(80);
		}
	}
</style>

